import clsx from 'clsx'
import BaseManagementComponent from 'components/BaseManagementComponent'
import FilterSelect from 'components/InfiniteSelect/FilterSelect'
import InputSearchKeyword from 'components/InputSearchKeyword'
import { useStore } from 'context/store'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Tag } from 'primereact/tag'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import { RegistrationStatusEnum } from 'utils/constants/event'
import { formatPhone } from 'utils/helper/common-helpers'
import { getRegistrationStatusTag } from 'utils/helper/table'
import { useIsMobile } from 'utils/hooks/useIsMobile'
import { useObjectSearchParams } from 'utils/hooks/useObjectSearchParams'
import DialogScanCode from './DialogScanCode'
import PrintPdf from './PrintPdf'

const normalizeStatusParam = (s: unknown): string[] => {
  if (Array.isArray(s)) return s as string[]
  if (typeof s === 'string' && s.includes(','))
    return s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
  if (typeof s === 'string' && s) return [s]
  return []
}

const RegistrationList = () => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const {
    eventRegistrationStore: {
      loadingListing,
      handleFilterDataChange,
      filterData,
      currentEvent,
      loadingDetail,
      listData: { data, meta },
      exportListRegistration,
      bulkCheckIn,
      loadingSubmit,
      setContextId,
      contextId,
    },
  } = useStore()

  const { id } = useParams()
  useEffect(() => {
    setContextId(id)
    return () => {
      setContextId(null)
    }
  }, [id, setContextId])
  const [showPrint, setShowPrint] = useState(false)
  const [showScanDialog, setShowScanDialog] = useState(false)
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [mobileKeyword, setMobileKeyword] = useState('')
  const [mobileStatus, setMobileStatus] = useState<string[]>([])
  const [mobileTimeslotId, setMobileTimeslotId] = useState<string | null>(null)

  const { setRestSearchObject, searchObject } = useObjectSearchParams()

  const registrationStatusOptions = useMemo(
    () =>
      Object.values(RegistrationStatusEnum).map((e) => ({
        label: t(e).toUpperCase(),
        value: e,
      })),
    [t]
  )

  useEffect(() => {
    setMobileKeyword((searchObject?.keyword as string) || '')
    setMobileStatus(normalizeStatusParam(searchObject?.status))
    setMobileTimeslotId((searchObject?.timeslotId as string) || null)
  }, [searchObject?.keyword, searchObject?.status, searchObject?.timeslotId])

  return contextId ? (
    <BaseManagementComponent
      dataSource={data}
      loading={loadingListing}
      actionColumns={[
        {
          key: 'view',
          icon: <i className="isax-eye"></i>,
          href: (item) => `${item._id}`,
          tooltip: t('View'),
        },
      ]}
      columns={[
        {
          key: 'name',
          dataIndex: 'name',
          header: t('Name'),
          width: '15%',
        },
        {
          key: 'furigana',
          header: t('Furigana'),
          sortable: true,
          width: '15%',
        },
        {
          key: 'email',
          header: t('Email'),
          width: '24%',
        },
        {
          key: 'phoneNumber',
          header: t('Phone number'),
          body: (data) => formatPhone(data.phoneNumber),
          width: '140px',
        },
        {
          key: 'registedTime',
          header: t('時間枠'),
          sortable: true,
          body: (data) =>
            `${dayjs(data.timeslot?.startTime).format(DATE_TIME_FORMAT.HOUR)}-${dayjs(data.timeslot?.endTime).format(DATE_TIME_FORMAT.HOUR)}`,
          width: '10%',
        },
        {
          key: 'guest',
          header: t('Guest'),
          width: '10%',
        },
        {
          key: 'status',
          filter: true,

          filterElement: (options) => {
            return (
              <FilterSelect
                options={registrationStatusOptions}
                className="w-20rem"
                onSelectItem={(_, value) => {
                  options.filterCallback(value, options.index)
                }}
                showSearch={false}
                multiple={true}
                value={options.value}
                onChange={(e) => options.filterApplyCallback(e, options.index)}
                placeholder={t('Select One')}
              />
            )
          },
          showFilterMatchModes: false,
          filterField: 'status',
          filterMatchMode: 'contains',
          body: (item) => getRegistrationStatusTag(item.status),
          header: t('Status'),
          width: '100px',
        },
      ]}
      actionWidth={'8%'}
      pagination={{
        ...meta,
      }}
      handleFilterDataChange={handleFilterDataChange}
      mobileListRender={(item) => (
        <Link
          to={`${item._id}`}
          className="border-1 surface-border border-round-lg p-3 block text-color no-underline cursor-pointer hover:surface-50 transition-duration-150"
        >
          <div className="flex justify-content-between align-items-start gap-2 mb-2">
            <div className="font-semibold text-lg line-height-3">
              {item.name}
            </div>
            {getRegistrationStatusTag(item.status)}
          </div>
          {item.furigana ? (
            <div className="text-sm text-color-secondary mb-1">
              {item.furigana}
            </div>
          ) : null}
          <div
            className="text-sm mb-1 white-space-normal"
            style={{ wordBreak: 'break-word' }}
          >
            {item.email}
          </div>

          <div className="text-sm mb-1">
            <span className="font-medium mr-2">{t('Phone number')}:</span>
            {formatPhone(item.phoneNumber)}
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium mr-2">{t('時間枠')}:</span>
            {item.timeslot
              ? `${dayjs(item.timeslot?.startTime).format(DATE_TIME_FORMAT.HOUR)}-${dayjs(item.timeslot?.endTime).format(DATE_TIME_FORMAT.HOUR)}`
              : '-'}
          </div>
          <div className="text-sm">
            <span className="font-medium mr-2">{t('Guest')}:</span>
            {item.guest}
          </div>
        </Link>
      )}
      filterComponent={
        <>
          <Dialog
            onHide={() => {
              setShowPrint(false)
            }}
            visible={showPrint}
            draggable={false}
            resizable={false}
            className={
              isMobile
                ? 'mobile-select-branch-dialog m-0 shadow-none border-noround'
                : undefined
            }
            contentClassName={isMobile ? 'p-0 h-full' : undefined}
            style={
              isMobile
                ? {
                    width: '100vw',
                    maxWidth: '100vw',
                    height: '100vh',
                    maxHeight: '100vh',
                  }
                : { width: '80vw', height: '70vh' }
            }
          >
            <PrintPdf
              filterData={filterData}
              currentEvent={currentEvent}
              currentTimeSlot={currentEvent?.timeslots?.find(
                (e) => e._id === searchObject.timeslotId
              )}
            />
          </Dialog>
          <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
            <h1 className="text-2xl md:text-3xl m-0 text-primary">
              {t('Registrations List')}
            </h1>

            <div className="flex flex-column md:flex-row gap-3 md:gap-4 w-full md:w-auto md:align-items-center">
              {!isMobile ? (
                <div className="w-full md:w-20rem">
                  <InputSearchKeyword placeholder={t('Search')} />
                </div>
              ) : (
                <Button
                  type="button"
                  icon="isax isax-filter-search"
                  label={t('Filter')}
                  className="w-full"
                  severity="secondary"
                  onClick={() => setShowMobileFilter(true)}
                />
              )}

              <Button
                icon="isax-direct-inbox"
                label={t('Download list')}
                loading={loadingDetail}
                outlined
                onClick={exportListRegistration}
                className="w-full md:w-auto"
              >
                &nbsp;
              </Button>

              <Button
                onClick={() => setShowPrint(true)}
                icon="isax-printer"
                outlined
                label={t('Print list')}
                className="w-full md:w-auto"
              >
                &nbsp;
              </Button>
            </div>
          </div>
          {currentEvent?.title ? (
            <>
              <h2 className="text-xl">
                {t('Event')}: {currentEvent?.title}
              </h2>
              <div
                className={clsx(
                  'flex flex-wrap gap-4',
                  !isMobile ? 'justify-content-between' : 'flex-column'
                )}
              >
                <div className="flex flex-column sm:flex-row flex-wrap gap-4">
                  <div className="flex gap-2 align-items-center">
                    <Tag
                      className="px-4 text-base uppercase"
                      severity="warning"
                    >
                      {t('CNOR')}
                    </Tag>
                    <p className="m-0 font-medium">
                      {currentEvent?.registeredQuantityEvent ?? 0} /{' '}
                      {currentEvent.maxRegistrationsEvent ?? 0}
                    </p>
                  </div>
                  <div className="flex gap-2 align-items-center">
                    <Tag className="p-tag-checked-in uppercase px-4 text-base">
                      {t('CHECKED_IN')}
                    </Tag>
                    <p className="m-0 font-medium">
                      {currentEvent?.checkedInCount ?? 0}
                    </p>
                  </div>
                  <div className="flex gap-2 align-items-center">
                    <Tag
                      className="px-4 text-base uppercase"
                      severity="success"
                    >
                      {t('Registed')}
                    </Tag>
                    <p className="m-0 font-medium">
                      {currentEvent?.registeredCount ?? 0}
                    </p>
                  </div>

                  <div className="flex gap-2 align-items-center">
                    <Tag className="p-tag-purpil uppercase px-4 text-base">
                      {t('Invalid mail')}
                    </Tag>
                    <p className="m-0 font-medium">
                      {currentEvent?.invalidMailCount ?? 0}
                    </p>
                  </div>
                  <div className="flex gap-2 align-items-center">
                    <Tag className="p-tag-inactive uppercase px-4 text-base">
                      {t('Canceled')}
                    </Tag>
                    <p className="m-0 font-medium">
                      {currentEvent?.canceledCount ?? 0}
                    </p>
                  </div>
                </div>
                {!isMobile ? (
                  <div className="w-full md:w-auto">
                    <Dropdown
                      className="w-full md:w-20rem"
                      placeholder={t('Select time slot')}
                      value={searchObject?.timeslotId}
                      onChange={(e) => {
                        setRestSearchObject({
                          timeslotId: e.target.value,
                        })
                      }}
                      showClear
                      options={[
                        ...currentEvent?.timeslots.map((item) => ({
                          value: item._id,
                          label: `${dayjs(item.startTime).format(DATE_TIME_FORMAT.FULL)} - ${dayjs(item.endTime).format(DATE_TIME_FORMAT.FULL)}`,
                        })),
                      ]}
                    />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-column md:flex-row gap-3 md:justify-content-between md:align-items-end mt-3">
                <div className="flex flex-column md:flex-row gap-2 justify-content-start md:align-items-end w-full md:w-auto">
                  <Button
                    type="button"
                    icon="isax isax-scan-barcode"
                    label={t('Scan QR code')}
                    className="w-full md:w-auto h-auto"
                    onClick={() => setShowScanDialog(true)}
                  />

                  <div className="text-red-600 text-xs">
                    ※QRコードスキャナーを接続した場合、そのまま使用できます。
                    <br />
                    ※スマートフォンのカメラを使用する場合は、カメラへのアクセス権限を付与してください。
                  </div>
                </div>
                <Button
                  type="button"
                  icon="isax isax-tick-circle"
                  label={t('Check in all')}
                  className="w-full md:w-auto h-auto"
                  loading={loadingSubmit}
                  onClick={() => {
                    confirmDialog({
                      message: t('doyouwant', {
                        action: t('Check in all'),
                      }),
                      header: t('confirmation', {
                        action: t('Check in all'),
                      }),
                      className: 'registration-check-in-all-confirm',
                      acceptLabel: t('Yes'),
                      rejectLabel: t('No'),
                      accept: bulkCheckIn,
                    })
                  }}
                />
              </div>
            </>
          ) : null}
          <DialogScanCode
            visible={showScanDialog}
            onHide={() => setShowScanDialog(false)}
            eventTitle={currentEvent?.title}
            eventId={id!}
            staffTimeslotId={(searchObject?.timeslotId as string) || null}
            onCheckInSuccess={() => handleFilterDataChange(filterData)}
          />
          <Dialog
            visible={showMobileFilter}
            onHide={() => setShowMobileFilter(false)}
            draggable={false}
            resizable={false}
            modal
            header={t('Filter')}
            className="mobile-select-branch-dialog m-0 shadow-none border-noround"
            contentClassName="p-0 h-full"
            style={{
              width: '100vw',
              maxWidth: '100vw',
              height: '100vh',
              maxHeight: '100vh',
            }}
          >
            <div className="w-full h-full  p-3 relative">
              <div className="card m-0 p-3 flex flex-column gap-3">
                <div>
                  <label className="font-semibold mb-2 block">
                    {t('Keyword')}
                  </label>
                  <InputText
                    value={mobileKeyword}
                    onChange={(e) => setMobileKeyword(e.target.value)}
                    placeholder={t('Search')}
                    className="w-full"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="font-semibold mb-2 block">
                    {t('Status')}
                  </label>
                  <FilterSelect
                    options={registrationStatusOptions}
                    className="w-full"
                    onSelectItem={(_, selectedValues) => {
                      setMobileStatus(selectedValues || [])
                    }}
                    showSearch={false}
                    multiple={true}
                    value={mobileStatus}
                    showFooter={false}
                    placeholder={t('Select One')}
                  />
                </div>
                {currentEvent?.timeslots?.length ? (
                  <div>
                    <label className="font-semibold mb-2 block">
                      {t('Select time slot')}
                    </label>
                    <Dropdown
                      className="w-full"
                      placeholder={t('Select time slot')}
                      value={mobileTimeslotId}
                      onChange={(e) => setMobileTimeslotId(e.target.value)}
                      showClear
                      options={currentEvent.timeslots.map((item) => ({
                        value: item._id,
                        label: `${dayjs(item.startTime).format(DATE_TIME_FORMAT.FULL)} - ${dayjs(item.endTime).format(DATE_TIME_FORMAT.FULL)}`,
                      }))}
                    />
                  </div>
                ) : null}
              </div>
              <div className="fixed left-0 right-0 bottom-0 p-3 bg-white border-top-1 surface-border flex gap-3">
                <Button
                  type="button"
                  outlined
                  label={t('Clear')}
                  className="flex-1"
                  onClick={() => {
                    setMobileKeyword('')
                    setMobileStatus([])
                    setMobileTimeslotId(null)
                    setRestSearchObject({
                      keyword: null,
                      status: null,
                      timeslotId: null,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
                <Button
                  type="button"
                  label={t('Apply')}
                  className="flex-1"
                  onClick={() => {
                    setRestSearchObject({
                      keyword: mobileKeyword.trim() || null,
                      status: mobileStatus.length ? mobileStatus : null,
                      timeslotId: mobileTimeslotId || null,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
              </div>
            </div>
          </Dialog>
        </>
      }
    />
  ) : null
}
export default observer(RegistrationList)
